import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { costAccountingApi } from '../lib/cost-accounting-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { CostAccountingResponse } from '../types/cost-accounting.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business React hook for cost accounting calculation. Uses useMutation since calculation is an explicit user action (POST), not automatic data fetch. Caches result per month.
 * @graph-connects costAccountingApi [calls] Cost accounting API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useCostAccountingCalculation() {
  const queryClient = useQueryClient();

  return useMutation<CostAccountingResponse, Error, string>({
    mutationFn: (month: string) => costAccountingApi.calculate(month),
    onSuccess: (data, month) => {
      queryClient.setQueryData(queryKeys.costAccounting.calculate(month), data);
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business React hook for BQ table sync status. Uses useQuery for automatic refetch.
 * @graph-connects costAccountingApi [calls] Cost accounting API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useSyncStatus(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.costAccounting.syncStatus,
    queryFn: () => costAccountingApi.syncStatus(),
    enabled,
    staleTime: 60_000,
  });
}
